import { StaffAccessNotice } from "@/components/admin/StaffAccessNotice";
import { createFileRoute } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { Suspense } from "react";

import { SiteHeader } from "@/components/site/SiteHeader";
import { AdminNav } from "@/components/admin/AdminNav";
import { Skeleton } from "@/components/ui/skeleton";
import { getAnalytics } from "@/lib/admin-customers.functions";

const q = queryOptions({
  queryKey: ["admin", "analytics"],
  queryFn: () => getAnalytics(),
  staleTime: 60_000,
});

export const Route = createFileRoute("/_authenticated/admin/analytics")({
  head: () => ({ meta: [{ title: "Analytics — Admin" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: AnalyticsPage,
  errorComponent: ({ error }) => <main className="mx-auto max-w-3xl px-4 py-10"><StaffAccessNotice error={error} /></main>,
});

function AnalyticsPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <h1 className="font-display text-4xl font-semibold">Analytics</h1>
        <p className="mt-1 text-sm text-muted-foreground">Métricas de los últimos 30 días.</p>
        <div className="mt-6"><AdminNav /></div>
        <Suspense fallback={<Skeleton className="mt-8 h-96 w-full" />}>
          <Body />
        </Suspense>
      </div>
    </div>
  );
}

function Body() {
  const { data } = useSuspenseQuery(q);
  const maxBookings = Math.max(1, ...data.series.map((s: any) => s.bookings));

  const kpis = [
    { label: "Reservas (30d)", value: data.totalBookings },
    { label: "Pagadas", value: data.paidCount },
    { label: "Ingresos MXN", value: `$${Number(data.totalRevenue).toLocaleString("es-MX", { maximumFractionDigits: 0 })}` },
    { label: "Bokun / Propias", value: `${data.bokunCount} / ${data.ownCount}` },
  ];

  return (
    <div className="mt-8 space-y-8">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {kpis.map((k) => (
          <div key={k.label} className="rounded-2xl border bg-card p-5">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">{k.label}</div>
            <div className="mt-2 font-display text-2xl font-semibold">{k.value}</div>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border bg-card p-6">
        <h2 className="font-display text-xl font-semibold">Reservas por día</h2>
        {data.series.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground">Sin datos en los últimos 30 días.</p>
        ) : (
          <div className="mt-6 flex items-end gap-1 h-48">
            {data.series.map((s: any) => (
              <div key={s.day} className="flex flex-1 flex-col items-center gap-1" title={`${s.day}: ${s.bookings}`}>
                <div
                  className="w-full rounded-t bg-primary/80"
                  style={{ height: `${(s.bookings / maxBookings) * 100}%`, minHeight: 2 }}
                />
                <span className="text-[10px] text-muted-foreground">{s.day.slice(5)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-2xl border bg-card p-6">
        <h2 className="font-display text-xl font-semibold">Top tours</h2>
        {data.topTours.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground">Sin reservas suficientes.</p>
        ) : (
          <table className="mt-4 w-full text-sm">
            <thead className="text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="py-2">Tour</th>
                <th className="py-2">Reservas</th>
                <th className="py-2 text-right">Ingresos</th>
              </tr>
            </thead>
            <tbody>
              {data.topTours.map((t: any) => (
                <tr key={t.title} className="border-t">
                  <td className="py-2">{t.title}</td>
                  <td className="py-2">{t.bookings}</td>
                  <td className="py-2 text-right">${Number(t.revenue).toLocaleString("es-MX", { maximumFractionDigits: 0 })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
